<template>
    <div class="position-relative">
        <v-inline-loader secondary v-if="isLoading"/>
        <div class="stack-s">
            <advanced-settings
                :email="email"
                @input="updateCredentials"
                :validation-aggregator="validationAggregator"/>
        </div>
        <div class="grid-content">
            <div class="grid-item grid-item-full">
                <v-select
                    label="From"
                    placeholder="Type an email or placeholder"
                    :taggable="true"
                    :tag-validator="validateTag"
                    :allow-empty="true"
                    :value="email.from"
                    @input="updateFrom"
                    :validate="validate.from"
                    :options="fromPlaceholders"/>
            </div>
            <div class="grid-item grid-item-full">
                <v-select
                    :class="{'stack-s': !ccEnabled || !bccEnabled}"
                    label="To"
                    placeholder="Type an email or placeholder"
                    :multiple="true"
                    :taggable="true"
                    :tag-validator="validateTag"
                    :allow-empty="true"
                    :value="email.to"
                    :validate="validate.to"
                    @input="updateTo"
                    :options="toPlaceholders"/>
                <div class="grid-content grid-fitted" v-if="!ccEnabled || !bccEnabled">
                    <div class="grid-item">&nbsp;</div>
                    <div class="grid-item no-flex" v-if="!ccEnabled">
                        <r-button type="link" @click="enableCopy" class="inline-s">Add CC</r-button>
                    </div>
                    <div class="grid-item no-flex" v-if="!bccEnabled">
                        <r-button type="link" @click="enableBlindCopy">Add BCC</r-button>
                    </div>
                </div>
            </div>
            <div class="grid-item grid-item-full" v-if="ccEnabled">
                <v-select
                    label="CC"
                    placeholder="Type an email or placeholder"
                    :multiple="true"
                    :taggable="true"
                    :tag-validator="validateTag"
                    :allow-empty="true"
                    :value="email.cc"
                    :validate="validate.cc"
                    @input="updateCC"
                    :options="toPlaceholders"/>
            </div>
            <div class="grid-item grid-item-full" v-if="bccEnabled">
                <v-select
                    label="BCC"
                    placeholder="Type an email or placeholder"
                    :multiple="true"
                    :taggable="true"
                    :tag-validator="validateTag"
                    :allow-empty="true"
                    :value="email.bcc"
                    :validate="validate.bcc"
                    @input="updateBCC"
                    :options="toPlaceholders"/>
            </div>
            <div class="grid-item grid-item-full">
                <r-input label="Subject"
                         :value="email.subject"
                         :validate="validate.subject"
                         @input="updateSubject"
                         placeholder="Add a subject or placeholder"
                />
            </div>
            <div class="grid-item grid-item-full">
                <email-content
                    :email="email"
                    :event="systemEvent"
                    :validate="validate"
                    :placeholder-indexes="placeholderIndexes"
                    @input="updateContent"/>
            </div>
        </div>
        <div class="grid-content">
            <div class="grid-item">
                <v-checkbox
                    v-if="systemEvent.canAttachInvoice"
                    @input="updateInvoiceAttachment"
                    v-model="hasAttachedInvoice"
                    :value="true"
                    label="Attach Invoice"/>
                <span v-else>&nbsp;</span>
            </div>
            <div class="grid-item no-flex">
                <preview-email
                    :email="email"
                    :before-preview="isValid"/>
            </div>
        </div>
    </div>
</template>

<script>
    import {EmailModel} from '@/common/models/rules/actions/send-email-action-model';
    import EventModel from '@/common/models/rules/event-model';
    import PlaceholderIndexer from '@/common/spec/placeholder-indexer';
    import ValidationAggregator from '@/common/validation/validation-aggregator';
    import {
        vCheckbox,
        vIcon,
        vSelect,
    } from '@/components/form';
    import vInlineLoader from '@/components/v-inline-loader';
    import placeholderEmail from '@/components/vuelidate/validators/placeholder-email';
    import EmailContent from '@/modules/rules-engine/actions/send-email/components/content/email-content';
    import PreviewEmail from '@/modules/rules-engine/actions/send-email/components/content/preview-email';
    import AdvancedSettings from '@/modules/rules-engine/actions/send-email/components/credentials/advanced-settings';
    import {extractCustomFieldResources} from '@/modules/rules-engine/components/condition-builder/get-fields-from-pathways';

    export default {
        name: 'email-editor',
        components: {
            vCheckbox,
            vIcon,
            vSelect,
            vInlineLoader,
            AdvancedSettings,
            EmailContent,
            PreviewEmail,
        },
        props: {
            index: Number,
            /**
             * @type {EmailModel}
             */
            email: EmailModel,
            /**
             * @type {Vue|{}}
             */
            validationBus: Object,
            /**
             * Validation iteration for delegation to form components.
             */
            validationIteratee: Object,
            systemEventType: String,
        },
        computed: {
            customFieldResources() {
                return this.placeholderPathways
                    .reduce((result, {pathways: paths}) => result
                        .concat(extractCustomFieldResources(paths)), []);
            },
            validate() {
                // get the right index within the iteration
                return this.validationIteratee[this.index];
            },
            validationId() {
                return `email-editor-${this.index}`;
            },
        },
        data() {
            const systemEvent = new EventModel({eventType: this.systemEventType});

            return {
                systemEvent,
                validationAggregator: new ValidationAggregator(),
                busDelegate: null,
                placeholderPathways: systemEvent.placeholderPathways,
                fromPlaceholders: [...new Set([...this.email.senders, ...systemEvent.defaultEmailSenders])]
                    .map(value => ({value, label: value})),
                toPlaceholders: [...new Set([...this.email.receivers, ...systemEvent.defaultEmailReceivers])]
                    .map(value => ({value, label: value})),
                placeholderIndexes: [],
                isLoading: true,
                ccEnabled: this.email.hasCc,
                bccEnabled: this.email.hasBcc,
                hasAttachedInvoice: this.email.hasAttachedInvoice,
            };
        },
        methods: {
            triggerUpdate(emailVersion) {
                this.$emit('input', {index: this.index, emailVersion});
            },
            updateCredentials(credentials) {
                this.triggerUpdate(this.email.cloneSelf({credentials}));
            },
            updateTo(to) {
                this.triggerUpdate(this.email.cloneSelf({to}));
            },
            updateCC(cc) {
                this.triggerUpdate(this.email.cloneSelf({cc}));
            },
            updateBCC(bcc) {
                this.triggerUpdate(this.email.cloneSelf({bcc}));
            },
            updateFrom(from) {
                this.triggerUpdate(this.email.cloneSelf({from}));
            },
            updateContent({text, html}) {
                this.triggerUpdate(this.email.cloneSelf({text, html}));
            },
            updateSubject(subject) {
                this.triggerUpdate(this.email.cloneSelf({subject}));
            },
            updateInvoiceAttachment(enable) {
                if (enable) {
                    const invoiceAttachment = {
                        resourceType: 'invoice',
                        resourceId: this.systemEvent.invoiceAttachmentPlaceholder,
                    };
                    this.triggerUpdate(this.email.cloneSelf({attachments: [invoiceAttachment]}));
                } else {
                    this.triggerUpdate(this.email.cloneSelf({attachments: []}));
                }
            },
            /**
             * Fetch custom fields that could apply to the email placeholders
             * @return {Promise<void>}
             */
            async preloadCustomFields() {
                this.isLoading = true;
                const fetches = this.customFieldResources
                    .map(resource => this.$store.dispatch('rulesEngine/fetchCustomFields', resource));
                await Promise.all(fetches);
                this.isLoading = false;
            },
            buildPlaceholders() {
                const customFieldsStore = this.$store.getters['rulesEngine/customFields'];
                this.placeholderIndexes = this.placeholderPathways.map(({group, pathways, extraPlaceholders}) => ({
                    indexer: new PlaceholderIndexer(customFieldsStore, pathways, extraPlaceholders),
                    group,
                }));
            },
            enableCopy() {
                this.ccEnabled = true;
            },
            enableBlindCopy() {
                this.bccEnabled = true;
            },
            validateTag(tag) {
                const valid = placeholderEmail(tag);
                if (!valid) {
                    this.$toast.negative(`"${tag}" is not a valid placeholder or email address.`, {delay: 5000});
                }
                return valid;
            },
            registerValidations() {
                this.validationAggregator.registerComponent({
                    name: this.validationId,
                    rules: [
                        {
                            name: `Please provide an email address for the "from" field.`,
                            delegate: () => !this.validate.from.required,
                        },
                        {
                            name: `Please provide at least one email address for the "to" field.`,
                            delegate: () => !this.validate.to.required,
                        },
                        {
                            name: `Please specify a subject for the email.`,
                            delegate: () => !this.validate.subject.required,
                        },
                        {
                            name: `Please specify content for the email in either HTML or plain text format.`,
                            delegate: () => !this.validate.content,
                        },
                        {
                            name: `Please specify a weight for the split test between 0 and 100.`,
                            delegate: () => !this.validate.weight.between,
                        },
                    ],
                });
            },
            /**
             * Delegate used by the preview to check if the email content including the credentials
             * is valid, thus preventing the preview of bad data.
             * @return {boolean}
             */
            isValid() {
                this.validationBus.$emit('reset');
                this.validationBus.$emit('touch');
                this.validationBus.$emit('validate');
                this.validate.$touch();
                return !this.validate.$error && !this.validationAggregator.validateAggregated();
            },
        },
        async created() {
            await this.preloadCustomFields();
            this.buildPlaceholders();
        },
        mounted() {
            this.busDelegate = () => this.validationBus.$emit(
                'result',
                {
                    index: this.index,
                    result: this.validationAggregator.validateAggregated(),
                },
            );
            this.validationBus.$on('validate', this.busDelegate);
            this.registerValidations();
        },
        beforeDestroy() {
            this.validationBus.$off('validate', this.busDelegate);
            this.validationAggregator.deregisterComponent(this.validationId);
        },
    };
</script>
